<template>
	<app-page>
		<app-tabs app :tabs="tabs">
			<template v-slot:tab-item="{ tab }">
				<app-tab-item :tab="tab" value="surveys">
					<app-heading :title="`${market.name} Surveys`">
						<template v-slot:left>
							<app-button v-if="$ravello.can('marketmath.post')"
								class="d-none d-sm-inline-flex"
								color="success"
								small
								outlined
								:to="{ name: 'market-survey', params: { survey: 'new' } }"
							>Add Survey</app-button>
						</template>
					</app-heading>
					<app-row>
						<app-cell>
							<app-data-table
								:items="surveys"
								:headers="headers"
								:total="count"
								:refresh.sync="refresh"
								:initial-state="tableState"
								@update="fetchSurveys"
								sort-by="_id"
								sort-desc
							>
								<template v-slot:status="{ item }">
									<span v-if="item.completed">
										<i class="success--text">Completed</i>
									</span>
									<span v-else-if="item.sent">
										<i class="primary--text">Sent To Participants</i>
									</span>
									<span v-else>
										<i class="primary--text">Draft</i>
									</span>
								</template>
								<template v-slot:progress="{ item }">
									<span
										:class="{
											[`${item.completed ?  'success' : 'primary'}--text`]: true,
										}"
									>
										{{item.completed_participants.length}}/{{item.participants.length}}
									</span>
								</template>
								<template v-slot:_id="{ item }">
									{{ item._id | datetime(market.timezone) }}
								</template>
								<template v-slot:sent_on="{ item }">
									<div>
										<span v-if="item.sent_on">
											{{ item.sent_on | datetime(market.timezone) }}
										</span>
										<span v-else>
											N/A
										</span>
									</div>
									<div v-if="item.reminded && item.reminded_on">
										<div>Reminded On</div>
										<div>{{ item.reminded_on | datetime(market.timezone) }}</div>
									</div>
								</template>
								<template v-slot:completed_on="{ item }">
									<span v-if="item.completed_on">
										{{ item.completed_on | datetime(market.timezone) }}
									</span>
									<span v-else>
										N/A
									</span>
								</template>
								<template v-slot:actions="{ item }">
									<app-button
										v-if="$ravello.can('marketmath.put') && !item.sent"
										color="secondary"
										icon="fa-paper-plane"
										table
										@click="onSend(item._id)"
									>
										Send
									</app-button>
									<app-button
										v-else-if="$ravello.can('marketmath.put')"
										color="secondary"
										:to="{ name: 'market-survey-results', params: { survey: item._id } }"
										table
										icon="fa-chart-pie"
										:disabled="!item.sent"
									>
										Results
									</app-button>
									<app-button
										v-if="$ravello.can('marketmath.put')"
										color="secondary"
										:to="{ name: 'market-survey', params: { survey: item._id } }"
										icon="fa-pencil-alt"
										table
									>
										Edit
									</app-button>
									<app-button-confirm
										v-if="$ravello.can('marketmath.delete')"
										color="error"
										icon="fa-trash"
										table
										label-confirm="Remove"
										:description="`Are you sure you want to remove survey`
											+ ` <b>${item.name}</b>?`"
										title="Remove Survey"
										@click="onDelete(item._id)"
									>
										Remove
									</app-button-confirm>
								</template>
							</app-data-table>
						</app-cell>
					</app-row>
					<app-floating-button
						class="d-inline-flex d-sm-none"
						:to="{ name: 'market-survey', params: { survey: 'new' } }"
						color="success"
						icon="fa-plus"
					/>
				</app-tab-item>
			</template>
		</app-tabs>
	</app-page>
</template>

<script>
import { filters, search, tableState } from '@/mixins'
import { marketMathTabs } from '@/datasources/tabs'

export default {
	mixins: [filters, search, tableState],
	data() {
		return {
			market: {
				name: '',
				timezone: 'UTC',
			},
			surveys: [],
			count: -1,
			refresh: false,
			tabs: [],
			headers: [{
				text: 'Status',
				value: 'status',
				sortable: false,
			}, {
				text: 'Created',
				value: '_id',
				sortable: true,
			}, {
				text: 'Name',
				value: 'name',
				sortable: true,
			}, {
				text: 'Progress',
				value: 'progress',
				sortable: false,
			}, {
				text: 'City',
				value: 'market_city.name',
				sortable: false,
			}, {
				text: 'Sent on',
				value: 'sent_on',
				sortable: true,
			}, {
				text: 'Completed on',
				value: 'completed_on',
				sortable: true,
			}, {
				text: 'Actions',
				value: 'actions',
				align: 'end',
				sortable: false,
			}],
			defaultTableSort: '-_id',
		}
	},
	computed: {
		marketId() {
			return this.$route.params.market
		},
	},
	async mounted() {
		this.market = await this.$ravello.graphql('market', this.marketId)
			.fields(['name', 'timezone'])
			.query()

		this.tabs = marketMathTabs(this.marketId)
	},
	methods: {
		async fetchSurveys(pagination) {
			this.updateTableState(pagination)

			const request = this.$ravello.graphql('surveys')
				.parent(this.$ravello.graphql('market', this.marketId))
				.fields(['_id', 'name', 'sent', 'completed', 'sent_on', 'completed_on',
					'participants', 'completed_participants', 'reminded', 'reminded_on'])
				.include(this.$ravello.graphql('market_city').field('name'))
				.fromPagination(pagination)

			if (this.count === -1) {
				request.count('count')
			}
			try {
				this.surveys = await request.query()
				if (this.count === -1) {
					this.count = request.results.count
				}
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},

		async onDelete(id) {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.v(2)
					.to('markets')
					.to(this.marketId)
					.to('surveys')
					.to(id)
					.delete()
				this.refresh = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},

		async onSend(id) {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.v(2)
					.to('markets')
					.to(this.marketId)
					.to('surveys')
					.to(id)
					.to('send')
					.create()
				this.refresh = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},
	},
}
</script>
